// src/race/RaceView.jsx
import React from "react";
import {
  Box,
  Tabs,
  Tab,
  Stack,
  Chip,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Grid,
  Avatar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import { AC_CAR_MODEL_MAP } from "../utils/cars";
import PenaltiesPanel from "./PenaltiesPanel";
/* =========================
 * Local helpers
 * ========================= */

const msToLap = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0 || ms === 2147483647 || ms === 999999999) return "—";
  const totalMs = Math.floor(ms);
  const minutes = Math.floor(totalMs / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);
  const millis = totalMs % 1000;
  return `${minutes}:${seconds.toString().padStart(2, "0")}.${millis.toString().padStart(3, "0")}`;
};
const safe = (x, fallback = "—") => (x === null || x === undefined || x === "" ? fallback : x);

function parseDateFromFilename(name) {
  if (!name) return null;
  const base = String(name).split("/").pop().replace(".json", "");
  const acMatch = base.match(/^(\d{4})_(\d{2})_(\d{2})_(\d{2})_(\d{2})_/);
  if (acMatch) {
    const [_, Y, M, D, h, m] = acMatch;
    const dt = new Date(Number(Y), Number(M) - 1, Number(D), Number(h), Number(m), 0);
    return isNaN(dt.getTime()) ? null : dt;
  }
  const accMatch = base.match(/^(\d{2})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_/);
  if (accMatch) {
    const [_, y, m, d, hh, mm, ss] = accMatch;
    const fullY = Number(y) + (Number(y) < 80 ? 2000 : 1900);
    const dt = new Date(fullY, Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss));
    return isNaN(dt.getTime()) ? null : dt;
  }
  const aceMatch = base.match(/^results_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_/i);
  if (aceMatch) {
    const [, year, month, day, hour, minute, second] = aceMatch;
    const dt = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    return isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}
function formatDate(dt) {
  if (!(dt instanceof Date) || isNaN(dt.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "long",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(dt);
  } catch {
    return dt.toLocaleString();
  }
}

function SplitsText({ splits }) {
  if (!Array.isArray(splits) || splits.length === 0) return <>—</>;
  return <>{splits.map((s) => msToLap(s)).join(" / ")}</>;
}

function MetaChips({ items = [] }) {
  return (
    <Stack flexWrap="wrap" sx={{m: 1}}>
      {items
        .filter(Boolean)
        .map((it, idx) =>
          typeof it === "string" ? (
            <Chip key={idx} label={it} size="large" sx={{ p: 1, fontSize: 14, marginBottom: 1 }} />
          ) : (
            <Chip key={idx} {...it} size="large" sx={{ p: 1, fontSize: 16, marginBottom: 1  }} />
          )
        )}
    </Stack>
  );
}

function driverId(row) {
  // All app driver routes use an S-prefixed id, while EVO exports raw Steam ids.
  const rawId = row?.playerId || row?.guid;
  if (!rawId || rawId === "â€”") return undefined;
  const id = String(rawId);
  return id.startsWith("S") ? id : `S${id}`;
}

function driverLabel(row, drivers) {
  const id = driverId(row);
  if (!id || !Array.isArray(drivers)) return row.driver ?? "—";
  return drivers.find((d) => d.id === id)?.name ?? row.driver ?? "—";
}

function driverMeta(row, drivers) {
  const id = driverId(row);
  if (!id || !Array.isArray(drivers)) return null;
  return drivers.find((d) => d.id === id) || null;
}

function carLabel(game, row) {
  if (game === "ACC" || game === "ACE") return safe(row.carModel);
  return safe(AC_CAR_MODEL_MAP[row.carModel]);
}

function pointsForPos(pos) {
  switch (pos) {
    case 1:
      return 25;
    case 2:
      return 18;
    case 3:
      return 15;
    case 4:
      return 12;
    case 5:
      return 10;
    case 6:
      return 8;
    case 7:
      return 6;
    case 8:
      return 4;
    case 9:
      return 2;
    case 10:
      return 1;
    default:
      return 0;
  }
}

/* =========================
 * Podium mini-cards
 * ========================= */
function Podium({ podium = [], game, drivers }) {
  if (!podium.length) return null;
  return (
    <Stack direction="row" spacing={2} sx={{ overflowX: "auto", pb: 2 }}>
      {podium.map((r, i) => {
        const meta = driverMeta(r, drivers);
        return (
          <Card
            key={i}
            sx={{
              minWidth: 280,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="overline"># {r.pos}</Typography>
                <Chip label={`${pointsForPos(r.pos)} pts`} size="small" color="primary" />
              </Stack>

              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
                <Avatar
                  src={meta?.image}
                  alt={`${meta?.name ?? r.driver ?? "Driver"} profile`}
                  sx={{ width: 40, height: 40 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }} noWrap>
                    {driverLabel(r, drivers)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }} noWrap>
                    {meta?.team ?? "—"}
                  </Typography>
                </Box>
              </Stack>

              <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }} noWrap>
                {carLabel(game, r)}
              </Typography>

              <Stack spacing={1} flexWrap="wrap">
                <Chip size="large" label={`#${r.raceNumber ?? r.carId ?? "—"}`} variant="outlined" />
                <Chip size="large" label={`Best ${safe(r.bestLap)}`} />
                {r.totalTime && <Chip size="large" label={`Total ${safe(r.totalTime)}`} />}
                <Chip size="large" label={`${safe(r.lapCount, 0)} laps`} />
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}

/* =========================
 * Mobile-first list rows
 * ========================= */

function LeaderboardRowCard({ r, i, game, drivers, onClick, dropped }) {
  const meta = driverMeta(r, drivers);
  const pts = pointsForPos(r.pos);

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        p: 1.5,
        "&:hover": { backgroundColor: "action.hover" },
        "&:active": { opacity: 0.85 },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Avatar
            src={meta?.image}
            alt={`${meta?.name ?? r.driver ?? "Driver"} profile`}
            sx={{ width: 140, height: 140, flex: "0 0 auto", margin: "auto" }}
          />
          <Typography sx={{ fontWeight: 900 }} noWrap>
            {driverLabel(r, drivers)}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {meta?.team ?? "—"} • {carLabel(game, r)}
          </Typography>

          <Stack spacing={1} sx={{ mt: 0.75, flexWrap: "wrap", rowGap: 0.75 }}>
            <Chip size="large" label={`Pos ${r.pos ?? i + 1}`} variant="outlined" />
            <Chip size="large" label={dropped ? `${pts} pts dropped` : `${pts} pts`} color={dropped ? "default" : "primary"} variant={dropped ? "outlined" : "filled"} />
            <Chip size="large" label={`#${r.raceNumber ?? r.carId ?? "—"}`} variant="outlined" />
            <Chip size="large" label={`Best ${safe(r.bestLap)}`} />
            <Chip size="large" label={`Last ${safe(r.lastLap)}`} />
            <Chip size="large" label={`${safe(r.lapCount, 0)} laps`} />
            {game === "AC" && (
              <>
                <Chip size="large" label={`B ${safe(r.ballast, 0)}`} />
                <Chip size="large" label={`R ${safe(r.restrictor, 0)}`} />
              </>
            )}
          </Stack>
        </Box>
      </Stack>
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
}

function LapsRowCardACC({ lp }) {
  return (
    <Box sx={{ p: 1.5 }}>
      <Stack spacing={0.5}>
        <Typography sx={{ fontWeight: 900 }} noWrap>
          Car #{safe(lp.carId, "—")} • DriverIdx {safe(lp.driverIndex, "—")}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          Lap: {msToLap(lp.laptime)} • Valid: {lp.isValidForBest ? "Yes" : "No"}
        </Typography>
        <Stack spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
          <Chip size="large" label={`Splits: ${Array.isArray(lp.splits) ? lp.splits.map(msToLap).join(" / ") : "—"}`} />
        </Stack>
      </Stack>
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
}

function LapsRowCardAC({ lp, onClick }) {
  return (
    <Box onClick={onClick} sx={{ p: 1.5, cursor: "pointer", "&:active": { opacity: 0.85 } }}>
      <Stack spacing={0.5}>
        <Typography sx={{ fontWeight: 900 }} noWrap>
          {safe(lp.DriverName)} • Car #{safe(lp.CarId, "—")}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          Lap: {msToLap(lp.LapTime ?? lp.Lap)} • Cuts: {safe(lp.Cuts, 0)} • Tyre: {safe(lp.Tyre, "—")}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
          <Chip
            size="small"
            label={`Splits: ${
              Array.isArray(lp.Sectors) && lp.Sectors.length ? lp.Sectors.map(msToLap).join(" / ") : "—"
            }`}
          />
        </Stack>
      </Stack>
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
}

function EntrantRowCard({ c, onClick }) {
  return (
    <Box onClick={onClick} sx={{ p: 1.5, cursor: "pointer", "&:active": { opacity: 0.85 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ width: 34, textAlign: "center", fontWeight: 900, opacity: 0.9 }}>
          #{safe(c.CarId, "—")}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 900 }} noWrap>
            {safe(c.Driver?.Name)}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {safe(c.Model)} • {safe(c.Skin)}
          </Typography>
          <Stack spacing={1} sx={{ mt: 0.75, flexWrap: "wrap", rowGap: 0.75 }}>
            <Chip size="large" label={`Ballast ${safe(c.BallastKG, 0)}`} />
            <Chip size="large" label={`Restrictor ${safe(c.Restrictor, 0)}`} />
          </Stack>
        </Box>
      </Stack>
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
}

function EventRowCard({ ev, onClick }) {
  return (
    <Box onClick={onClick} sx={{ p: 1.5, cursor: "pointer", "&:active": { opacity: 0.85 } }}>
      <Stack spacing={1.5} alignItems="center">
        <Chip size="large" label={safe(ev.Type, "—")} variant="outlined" />
        <Typography sx={{ fontWeight: 900 }} noWrap>
          Car #{safe(ev.CarId, "—")} • {safe(ev.Driver?.Name)}
        </Typography>
      </Stack>
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
}

/* =========================
 * Main Race View
 * ========================= */
export default function RaceView({ data, fileName, drivers = [], standings = [] }) {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState(0);
  const [filter, setFilter] = React.useState("");

  if (!data) return null;

  if (!data.ok) {
    return (
      <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Stack spacing={1}>
          <Typography variant="h6">{fileName}</Typography>
          <Typography color="error">{data.error || "Failed to parse session"}</Typography>
        </Stack>
      </Paper>
    );
  }

  const {
    summary,
    leaderboard = [],
    laps = [],
    cars = [],
    events = [],
    penalties = [],
    postPenalties = [],
  } = data;

  const when = formatDate(parseDateFromFilename(fileName));
  const headerChips = [
    when && { label: when, icon: <InfoOutlinedIcon sx={{ fontSize: 14 }} /> }, 
    `Entries: ${safe(summary.entries, 0)}`,
    { label: `Best Lap: ${safe(summary.overallBestLap, "—")} - ${safe(summary.overallBestDriver, "")}`, color: "primary" },
  ].filter(Boolean);

  const extraChips =
    data.game === "ACC" || data.game === "ACE"
      ? [
          summary.serverName && `${summary.serverName}`,
          typeof summary.isWet === "boolean" ? (summary.isWet ? "Wet" : "Dry") : null,
        ].filter(Boolean)
      : [
          Number.isFinite(summary.carsCount) && `Cars: ${summary.carsCount}`,
          Number.isFinite(summary.lapsCount) && `Laps: ${summary.lapsCount}`,
          Number.isFinite(summary.eventsCount) && `Events: ${summary.eventsCount}`,
        ].filter(Boolean);

  const podium = leaderboard.slice(0, 3);
  const filteredRows = leaderboard.filter((r) => {
    if (!filter) return true;
    const name = driverLabel(r, drivers).toLowerCase();
    const car = carLabel(data.game, r).toLowerCase();
    return name.includes(filter.toLowerCase()) || car.includes(filter.toLowerCase());
  });

  const onRowClick = (r) => {
    const id = driverId(r);
    if (id) navigate(`/driver/${id}`);
  };
  const isDropped = (r) => {
    const normalized = String(driverId(r) || "").replace(/^S/, "");
    const standing = standings.find((d) => String(d.id || "").replace(/^S/, "") === normalized);
    return standing?.dropped_round_files?.includes(fileName) === true;
  };

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        background: "#ffffff09",
      }}
    >
      {/* Header */}
      <Stack spacing={1} sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6" sx={{ fontWeight: 800, mr: 1 }}>
            {safe(summary.type, "Session")}
          </Typography>
        </Stack>
        <MetaChips items={headerChips} />
        {extraChips.length > 0 && <MetaChips items={extraChips} />}
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Leaderboard" />
          <Tab label="Laps" />
          {data.game === "ACC" || data.game === "ACE" ? <Tab label="Penalties" /> : <Tab label="Entrants" />}
        </Tabs>
      </Box>

      {/* Leaderboard tab */}
      {tab === 0 && (
        <Box>
          <TextField
            size="small"
            placeholder="Filter by driver or car…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ mb: 2, width: { xs: "100%", sm: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Mobile-first list */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
              {filteredRows.map((r, i) => (
                <LeaderboardRowCard
                  key={`lb-xs-${i}`}
                  r={r}
                  i={i}
                  game={data.game}
                  drivers={drivers}
                  onClick={() => onRowClick(r)}
                  dropped={isDropped(r)}
                />
              ))}
            </Paper>
          </Box>

          {/* Desktop/tablet table (aligned styling: avatar+name in cell, chips for key stats) */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <Table size="small" sx={{ minWidth: data.game === "ACC" ? 1100 : 980 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Pos</TableCell>
                    <TableCell>#</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Car</TableCell>
                    <TableCell>Laps</TableCell>
                    <TableCell>Best</TableCell>
                    <TableCell>Last</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Points</TableCell>
                    {data.game === "ACC" && <TableCell>Best Splits</TableCell>}
                    {data.game === "ACC" && <TableCell>Last Splits</TableCell>}
                    {data.game === "AC" && (
                      <>
                        <TableCell align="right">Ballast</TableCell>
                        <TableCell align="right">Restrictor</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((r, i) => {
                    const meta = driverMeta(r, drivers);
                    return (
                      <TableRow
                        key={`lb-md-${i}`}
                        onClick={() => onRowClick(r)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ fontWeight: 900 }}>{r.pos}</TableCell>
                        <TableCell>{r.raceNumber ?? r.carId ?? "—"}</TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Avatar
                              src={meta?.image}
                              alt={`${meta?.name ?? r.driver ?? "Driver"} profile`}
                              sx={{ width: 36, height: 36 }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 900 }} noWrap>
                                {driverLabel(r, drivers)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {meta?.team ?? "—"}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>{carLabel(data.game, r)}</TableCell>
                        
                        <TableCell align="right">{safe(r.lapCount, "")}</TableCell>

                        <TableCell>{safe(r.bestLap)}</TableCell>

                        <TableCell>{safe(r.lastLap)}</TableCell>

                        <TableCell>{safe(r.totalTime)}</TableCell>

                        <TableCell>
                          <Chip size="large" label={isDropped(r) ? `${pointsForPos(r.pos)} pts dropped` : `${pointsForPos(r.pos)} pts`} color={isDropped(r) ? "default" : "primary"} variant={isDropped(r) ? "outlined" : "filled"} />
                        </TableCell>

                        {data.game === "ACC" && (
                          <TableCell>
                            <SplitsText splits={r.bestSplits} />
                          </TableCell>
                        )}

                        {data.game === "ACC" && (
                          <TableCell>
                            <SplitsText splits={r.lastSplits} />
                          </TableCell>
                        )}

                        {data.game === "AC" && (
                          <>
                            <TableCell align="right">{safe(r.ballast, 0)}</TableCell>
                            <TableCell align="right">{safe(r.restrictor, 0)}</TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>
      )}

      {/* Laps tab */}
      {tab === 1 && (
        <Box>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Laps</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* xs list cards */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
                  {laps.map((lp, i) =>
                    data.game === "ACC" || data.game === "ACE" ? (
                      <LapsRowCardACC key={`lap-xs-${i}`} lp={lp} />
                    ) : (
                      <LapsRowCardAC
                        key={`lap-xs-${i}`}
                        lp={lp}
                        onClick={() => navigate(`/driver/S${lp.DriverGuid}`)}
                      />
                    )
                  )}
                </Paper>
              </Box>

              {/* md+ table */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Table size="small" sx={{ width: "100%", overflowX: "auto", display: "block" }}>
                  <TableHead>
                    <TableRow>
                      {data.game === "ACC" || data.game === "ACE" ? (
                        <>
                          <TableCell>#Car</TableCell>
                          <TableCell>DriverIdx</TableCell>
                          <TableCell>Lap Time</TableCell>
                          <TableCell>Splits</TableCell>
                          <TableCell>Valid for Best</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>Driver</TableCell>
                          <TableCell>#Car</TableCell>
                          <TableCell>Lap Time</TableCell>
                          <TableCell>Splits</TableCell>
                          <TableCell>Cuts</TableCell>
                          <TableCell>Tyre</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {laps.map((lp, i) =>
                      data.game === "ACC" || data.game === "ACE" ? (
                        <TableRow key={i}>
                          <TableCell>{safe(lp.carId, "—")}</TableCell>
                          <TableCell>{safe(lp.driverIndex, "—")}</TableCell>
                          <TableCell>{msToLap(lp.laptime)}</TableCell>
                          <TableCell>
                            <SplitsText splits={lp.splits} />
                          </TableCell>
                          <TableCell>{lp.isValidForBest ? "Yes" : "No"}</TableCell>
                        </TableRow>
                      ) : (
                        <TableRow
                          key={i}
                          onClick={() => navigate(`/driver/S${lp.DriverGuid}`)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{safe(lp.DriverName)}</TableCell>
                          <TableCell>{safe(lp.CarId, "—")}</TableCell>
                          <TableCell>{msToLap(lp.LapTime ?? lp.Lap)}</TableCell>
                          <TableCell>
                            {Array.isArray(lp.Sectors) && lp.Sectors.length ? (
                              <SplitsText splits={lp.Sectors} />
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>{safe(lp.Cuts, 0)}</TableCell>
                          <TableCell>{safe(lp.Tyre, "—")}</TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Penalties / Entrants */}
      {tab === 2 && (data.game === "ACC" || data.game === "ACE") && (
        <PenaltiesPanel penalties={penalties} drivers={drivers} event={data} />
      )}

      {tab === 2 && data.game === "AC" && (
        <Box>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Entrants (Cars)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* xs list */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
                  {cars.map((c, idx) => (
                    <EntrantRowCard
                      key={`car-xs-${idx}`}
                      c={c}
                      onClick={() => navigate(`/driver/S${c.Driver?.Guid}`)}
                    />
                  ))}
                </Paper>
              </Box>

              {/* md+ table */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Table size="small" sx={{ width: "100%", overflowX: "auto", display: "block" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>CarId</TableCell>
                      <TableCell>Driver</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell>Skin</TableCell>
                      <TableCell align="right">BallastKG</TableCell>
                      <TableCell align="right">Restrictor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cars.map((c, idx) => (
                      <TableRow
                        key={idx}
                        onClick={() => navigate(`/driver/S${c.Driver?.Guid}`)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>{safe(c.CarId)}</TableCell>
                        <TableCell>{safe(c.Driver?.Name)}</TableCell>
                        <TableCell>{safe(c.Model)}</TableCell>
                        <TableCell>{safe(c.Skin)}</TableCell>
                        <TableCell align="right">{safe(c.BallastKG, 0)}</TableCell>
                        <TableCell align="right">{safe(c.Restrictor, 0)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* AC Events */}
          {Array.isArray(events) && events.length > 0 && (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>Events</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* xs list */}
                <Box sx={{ display: { xs: "block", md: "none" } }}>
                  <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
                    {events.map((ev, idx) => (
                      <EventRowCard
                        key={`ev-xs-${idx}`}
                        ev={ev}
                        onClick={() => navigate(`/driver/S${ev.Driver?.Guid}`)}
                      />
                    ))}
                  </Paper>
                </Box>

                {/* md+ table */}
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <Table size="small" sx={{ width: "100%", overflowX: "auto", display: "block" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>#Car</TableCell>
                        <TableCell>Driver</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.map((ev, idx) => (
                        <TableRow
                          key={idx}
                          onClick={() => navigate(`/driver/S${ev.Driver?.Guid}`)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{safe(ev.Type)}</TableCell>
                          <TableCell>{safe(ev.CarId, "—")}</TableCell>
                          <TableCell>{safe(ev.Driver?.Name)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}
    </Paper>
  );
}
